import { useCallback, useContext, useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import TestElement, {
  ITestChoice,
  QuestionFontSize,
  QuestionTypes,
} from "@/shared/components/candidate/scenes/TestElement/TestElement";
import useSetScrollable from "@/modules/candidate/hooks/useSetScrollable.hook";
import StaticArrow from "@/ui-components/commonComponents/StaticArrow/StaticArrow";
import { InviteContext } from "../../contexts/invite.context";
import { SectionContext } from "../../contexts/section.context";
import { CurrentPageContext } from "../../contexts/current-page.context";
import { TimeLimitInfo } from "../TimeLimitInfo/TimeLimitInfo";
import { QuestionHeader } from "../QuestionHeader/QuestionHeader";
import { AssessmentProgress } from "../AssessmentProgress/AssessmentProgress";
import style from "./style.module.css";
import testElementStyle from "@/shared/components/candidate/scenes/TestElement/style.module.css";
import { ILocalizedTestElement } from "@/modules/assessments/types/test.interface";
import { GroupedTestElementsContainer } from "../GroupedTestElementsContainer/GroupedTestElementsContainer";
import { getResolvedElementText } from "../../utils/test-element-content.utils";
import MostLeastPairComponent from "@/ui-components/testElementComponents/MostLeastPairComponent/MostLeastPairComponent";

interface ICandidateTestElement {
  currentPage: number;
  isLoadingTestInstructions: boolean;
  currentAnswers: ITestChoice[];
  setIsOpenedInstructions: (value: boolean) => void;
  handleChoiceSelect: (value: ITestChoice) => void;
  timerDisabled: boolean;
}

export default function CandidateTestElement({
  currentPage,
  currentAnswers,
  isLoadingTestInstructions,
  setIsOpenedInstructions,
  handleChoiceSelect,
  timerDisabled,
}: ICandidateTestElement) {
  const { xxl, xl, lg } = useBreakpoint();
  const isDesktopView = xxl || xl || lg;
  const intl = useIntl();

  const scrollableContainer = useRef(null);

  const { hasScroll, handleScroll } = useSetScrollable(scrollableContainer.current);

  const { groupConfig } = useContext(InviteContext);
  const { questionsList } = useContext(CurrentPageContext);
  const { currentSectionElement, isTimedOutSection } = useContext(SectionContext);

  const groupSize = groupConfig?.size;
  const groupHeaderText = groupConfig?.header?.value ?? undefined;

  const segments = useMemo(() => partitionQuestions(questionsList, groupSize), [questionsList, groupSize]);

  const renderTestElements = useCallback((): React.ReactNode[] => {
    const currentPageSegments = segments[currentPage - 1] || [];
    const currentPageQuestions = questionsList[currentPage - 1] || [];
    const rendered = new Set<number>();
    const result: React.ReactNode[] = [];

    currentPageSegments.forEach((segment, index) => {
      if (segment.length === 1) {
        const element = segment[0];
        if (rendered.has(element.elementId)) return;

        const elementIndex = currentPageQuestions.findIndex((q) => q.elementId === element.elementId);
        const isMostLeast = element.config.type === QuestionTypes.MostLeast;
        const nextElement =
          elementIndex >= 0 && elementIndex < currentPageQuestions.length - 1
            ? currentPageQuestions[elementIndex + 1]
            : null;
        const isPair = isMostLeast && nextElement?.config.type === QuestionTypes.MostLeast;

        if (isPair && nextElement) {
          rendered.add(element.elementId);
          rendered.add(nextElement.elementId);

          const elements: [ILocalizedTestElement, ILocalizedTestElement] = [element, nextElement];
          const answers = [
            currentAnswers.find((answer) => answer.elementId === element.elementId)?.choiceKey,
            currentAnswers.find((answer) => answer.elementId === nextElement.elementId)?.choiceKey,
          ];

          result.push(
            <div
              key={`pair-${element.elementId}`}
              id={`test-element-${element.elementId}`}
              role="group"
              tabIndex={-1}
              aria-label={intl.formatMessage({ id: "candidate.a11y.questionPair", defaultMessage: "Question pair" })}
            >
              <div className={testElementStyle.answerChoicesContainer}>
                <MostLeastPairComponent
                  elements={elements}
                  answers={answers}
                  onChoiceSelect={handleChoiceSelect}
                  disable={isTimedOutSection}
                />
              </div>
            </div>,
          );
        } else {
          result.push(
            <TestElement
              key={element.elementId}
              id={element.elementId}
              disableChoices={isTimedOutSection}
              choices={element.config.payload.choices}
              currentAnswer={currentAnswers.find((answer) => answer.elementId === element.elementId)?.choiceKey}
              questionType={element.config.type}
              question={getResolvedElementText(element)}
              questionNumber={element.questionNumber}
              questionFontSize={QuestionFontSize.large}
              onChoiceSelect={handleChoiceSelect}
            />,
          );
        }
      } else {
        result.push(
          <GroupedTestElementsContainer
            key={index}
            segment={segment}
            groupHeaderText={groupHeaderText}
            isTimedOutSection={isTimedOutSection}
            currentAnswers={currentAnswers}
            handleChoiceSelect={handleChoiceSelect}
          />,
        );
      }
    });

    return result;
  }, [
    segments,
    currentPage,
    questionsList,
    currentAnswers,
    handleChoiceSelect,
    isTimedOutSection,
    groupHeaderText,
    intl,
  ]);

  const timeLimit = currentSectionElement?.config.startsSection?.timeLimit ?? 0;
  const isTimedSection = !timerDisabled && timeLimit > 0;

  return (
    <div className={style.testChildContainer}>
      {isTimedSection && <TimeLimitInfo timeLimit={timeLimit} />}

      <AssessmentProgress />

      <QuestionHeader
        setIsOpenedInstructions={setIsOpenedInstructions}
        isLoadingTestInstructions={isLoadingTestInstructions}
      />

      <div ref={scrollableContainer} className={style.testElementsContainer} onScroll={handleScroll}>
        <div id="first" />

        <div className={style.testElementsContainerFlex}>{renderTestElements()}</div>

        {!isDesktopView && hasScroll && <StaticArrow />}
      </div>
    </div>
  );
}

// Partitions an assessment's questions list into segments based on group
// size and section breaks for the GroupedTestElementsContainer component.
// Also assigns question numbers to each (non-instruction) test element within
// segments.
const partitionQuestions = (
  questionsList: ILocalizedTestElement[][],
  groupSize: number | undefined,
): ILocalizedTestElement[][][] => {
  const segments: ILocalizedTestElement[][][] = [];
  let questionNumber = 1;

  questionsList.forEach((pageOfQuestions) => {
    const currentPageSegments: ILocalizedTestElement[][] = [];
    let currentGroup: ILocalizedTestElement[] = [];

    pageOfQuestions.forEach((question) => {
      const shouldStartNewSection = question.config.type === QuestionTypes.Text && question.config.breakAfter;

      if (shouldStartNewSection) {
        questionNumber = 1;
        if (currentGroup.length > 0) {
          currentPageSegments.push([...currentGroup]);
          currentGroup = [];
        }
        currentPageSegments.push([question]);
      } else if (groupSize) {
        question.questionNumber = questionNumber;
        questionNumber++;
        currentGroup.push(question);
        if (currentGroup.length === groupSize) {
          currentPageSegments.push([...currentGroup]);
          currentGroup = [];
        }
      } else {
        currentPageSegments.push([question]);
      }
    });

    // If after partitioning all questions we have a remainder of questions
    // left over, push the final group of question into it's own segment
    if (currentGroup.length > 0 && groupSize) {
      currentPageSegments.push(currentGroup);
    }

    segments.push(currentPageSegments);
  });

  return segments;
};
